<template lang="pug" src="./videoConversionBadge.pug"></template>

<i18n>
{
    "de": {
        "Wird konvertiert": "Wird konvertiert",
        "Konvertierung fehlgeschlagen": "Konvertierung fehlgeschlagen",
        "Erneut versuchen": "Erneut versuchen"
    },
    "en": {
        "Wird konvertiert": "Converting",
        "Konvertierung fehlgeschlagen": "Conversion failed",
        "Erneut versuchen": "Retry"
    }
}
</i18n>

<script>
import _ from 'lodash';
import Echo from '@/echo';

/**
 * System-wide video HLS conversion progress overlay.
 *
 * Drops onto any video that's still converting. Reads the initial
 * `video.conversion_progress` from the GraphQL response on mount and, if it's
 * below 100, subscribes to the per-video Echo channel to get live updates.
 * On `video-conversion:completed` it fades the overlay out; on `:failed` it
 * turns red. Works the same for AI-generated videos and manual uploads.
 */
export default {
    name: 'VideoConversionBadge',
    props: {
        video: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            progress: null,
            failed: false,
            failureMessage: null,
            channel: null,
        };
    },
    computed: {
        videoId() {
            return _.get(this.video, 'id') || null;
        },
        hasHls() {
            return !!_.get(this.video, 'hls.id');
        },
        isConverting() {
            if (this.failed) return true;
            if (this.hasHls && this.progress === null) return false;
            return this.progress !== null && this.progress < 100;
        },
        displayProgress() {
            if (this.progress === null) return 0;
            return Math.max(0, Math.min(100, this.progress));
        },
        // HLS (segment-muxer) conversion does not report granular progress — ffmpeg's
        // onProgress only fires once at 100. So while a real intermediate value hasn't
        // arrived, show an indeterminate "Converting…" animation instead of a fake "0%".
        // If a future/longer conversion ever does report a real percentage (0 < p < 100),
        // we switch to the determinate bar automatically.
        isIndeterminate() {
            return this.progress === null || this.progress <= 0;
        },
    },
    mounted() {
        this.progress = _.get(this.video, 'conversion_progress', null);
        if (this.progress !== null && this.progress < 100) {
            this.subscribe();
        } else if (!this.hasHls && this.progress === 0) {
            this.subscribe();
        }
    },
    beforeDestroy() {
        this.unsubscribe();
    },
    methods: {
        subscribe() {
            if (!this.videoId) return;
            if (!Echo.client) Echo.initialize();

            this.channel = Echo.client.private(`video.${this.videoId}`);
            this.channel.listen('.video-conversion:progress', (payload) => {
                this.progress = payload.progress;
                this.failed = false;
            });
            this.channel.listen('.video-conversion:completed', (payload) => {
                this.progress = 100;
                this.$emit('completed', payload);
                this.unsubscribe();
            });
            this.channel.listen('.video-conversion:failed', (payload) => {
                this.failed = true;
                this.failureMessage = payload && payload.error ? payload.error : null;
                this.$emit('failed', payload);
            });
        },
        unsubscribe() {
            if (this.channel && this.videoId && Echo.client) {
                Echo.client.leave(`video.${this.videoId}`);
            }
            this.channel = null;
        },
        retry() {
            this.$emit('retry', { videoId: this.videoId });
        },
    },
};
</script>

<style lang="stylus" scoped>
// Full-tile scrim that obscures whatever is behind (incl. the tile's play button),
// with a compact centred card carrying the conversion state. Works at any tile size
// / aspect ratio.
.video-conversion-badge
    position absolute
    inset 0
    z-index 3
    display flex
    align-items center
    justify-content center
    padding 8px
    pointer-events none
    background rgba(20, 20, 28, 0.62)
    backdrop-filter blur(3px)
    color #fff
    transition opacity 0.3s ease

.vcb-card
    display flex
    flex-direction column
    align-items center
    gap 7px
    width 100%
    max-width 150px
    text-align center

.vcb-label
    font-size 12px
    font-weight 600
    line-height 1.2
    letter-spacing 0.01em

.vcb-bar
    width 100%
    height 5px
    background rgba(255, 255, 255, 0.22)
    border-radius 999px
    overflow hidden

.vcb-fill
    height 100%
    border-radius 999px
    background #6E26FF
    transition width 0.3s ease

// Indeterminate sweep — used while HLS conversion reports no granular progress.
.vcb-fill--indeterminate
    width 40%
    transition none
    animation vcb-indeterminate 1.1s ease-in-out infinite

@keyframes vcb-indeterminate
    0%
        margin-left -40%
    100%
        margin-left 100%

.vcb-percent
    font-size 13px
    font-weight 700
    font-variant-numeric tabular-nums

// Failed state — red scrim, interactive (retry).
.video-conversion-badge.is-failed
    background rgba(150, 30, 40, 0.66)
    pointer-events auto

    .vcb-icon
        font-size 22px
        margin-bottom 2px

    .vcb-retry
        margin-top 2px

// Mount/unmount fade (shared `fade` transition).
.fade-enter-active,
.fade-leave-active
    transition opacity 0.3s ease

.fade-enter,
.fade-leave-to
    opacity 0
</style>
